import { FTUE_FLOW_PRIORITY, registerSteps, useFtueAnchor, useFtueStore } from '@/ftue'
import { useCallback, useEffect } from 'react'

export const RESUME_BUILDER_JOURNEY_FLOW = 'resume-builder-journey'
export const RESUME_BUILDER_ELIGIBILITY_FLOW = 'resume-builder-eligibility'

export const MIN_MEANINGFUL_SUMMARY_LENGTH = 40

export function completesDirectEditFtue(path: string, value: string): boolean {
  return path === 'summary' && value.trim().length >= MIN_MEANINGFUL_SUMMARY_LENGTH
}

export function shouldGuideFirstResumeToBuilder(input: {
  eligible: boolean
  hasStarted: boolean
  hasReachedAgent: boolean
}): boolean {
  return !input.hasReachedAgent && (input.eligible || input.hasStarted)
}

export function completesRenameTitleFtue(title: string): boolean {
  const normalized = title.trim()
  return normalized.length > 0 && normalized.toLowerCase() !== 'untitled resume'
}

export const RESUME_BUILDER_JOURNEY_STEPS = {
  addResume: 'resume-builder-add-first-resume',
  buildResume: 'resume-builder-open-builder',
  startMethod: 'resume-builder-choose-start-method',
  renameTitle: 'resume-builder-rename-draft',
  directEdit: 'resume-builder-edit-resume-text',
  template: 'resume-builder-choose-template',
  score: 'resume-builder-review-score',
  chat: 'resume-builder-prompt-agent',
  drafts: 'resume-builder-open-drafts',
  continueToAgent: 'resume-builder-continue-job-agent',
} as const

const ANCHORS = {
  addResume: 'resume-builder-journey-add-resume',
  buildResume: 'resume-builder-journey-build-resume',
  startMethod: 'resume-builder-journey-start-method',
  renameTitle: 'resume-builder-journey-rename-title',
  directEdit: 'resume-builder-journey-direct-edit',
  template: 'resume-builder-journey-template',
  score: 'resume-builder-journey-score',
  chat: 'resume-builder-journey-chat',
  drafts: 'resume-builder-journey-drafts',
  continueToAgent: 'resume-builder-journey-continue',
} as const

const journeyStep = (
  id: string,
  anchorId: string,
  order: number,
  body: string,
  placement: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'bottom-start',
  options: {
    autoCompleteMs?: number
    allowAnchorInteraction?: boolean
    dismissLabel?: string
    dismissOnAction?: boolean
    hideDismiss?: boolean
    condition?: () => boolean
    overlayBody?: string
    overlayPlacement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'bottom-start'
  } = {},
) => {
  const { dismissLabel = 'Got it', overlayBody, ...stepOptions } = options
  return {
    id,
    flow: RESUME_BUILDER_JOURNEY_FLOW,
    // A phase per milestone keeps the shared coachmark renderer in its
    // single-concept mode, so this journey renders a short contextual acknowledgement
    // instead of Next/Back/Skip controls.
    phase: id,
    order,
    trigger: { type: 'immediate' as const },
    anchorId,
    presentation: 'coachmark' as const,
    placement,
    hideSkip: true,
    content: { body, dismissLabel, ...(overlayBody ? { overlayBody } : {}) },
    ...stepOptions,
  }
}

function registerJourney() {
  registerSteps([
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.addResume,
      ANCHORS.addResume,
      1,
      'Start by adding your first resume. We can build it with you, even if you do not have one ready yet.',
      'bottom',
      {
        autoCompleteMs: 3600,
        dismissLabel: 'Okay',
        condition: () => window.location.pathname === '/resume',
      },
    ),
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.buildResume,
      ANCHORS.buildResume,
      2,
      'Choose Build a resume and your resume agent will guide you from the first draft to a job-ready version.',
      'top',
      {
        dismissOnAction: true,
        // Action-only handoff: do not let an acknowledgement complete this step
        // while leaving the user on /resume. The highlighted Build a resume
        // control (or ESC to exit the whole tour) is the only way forward.
        hideDismiss: true,
        condition: () => window.location.pathname === '/resume',
      },
    ),
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.startMethod,
      ANCHORS.startMethod,
      3,
      'Pick the easiest way to begin: paste a job description, paste an existing resume, upload a PDF, or tell the agent about your background.',
      'bottom-start',
      {
        dismissLabel: 'Okay',
        allowAnchorInteraction: true,
        condition: () =>
          window.location.pathname === '/resume-builder' &&
          !new URLSearchParams(window.location.search).has('draft'),
      },
    ),
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.renameTitle,
      ANCHORS.renameTitle,
      3.5,
      'Give your draft a clear title so it is easy to recognize when you return to it later.',
      'bottom',
      {
        dismissLabel: 'I’ll rename it',
        allowAnchorInteraction: true,
        condition: () =>
          window.location.pathname === '/resume-builder' &&
          new URLSearchParams(window.location.search).has('draft'),
      },
    ),
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.directEdit,
      ANCHORS.directEdit,
      4,
      'Add a short sentence about your experience. Click the summary to start.',
      'top',
      {
        dismissLabel: 'Skip for now',
        allowAnchorInteraction: true,
        condition: () =>
          window.location.pathname === '/resume-builder' &&
          new URLSearchParams(window.location.search).has('draft'),
      },
    ),
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.template,
      ANCHORS.template,
      5,
      'Choose a style that fits you. Your content stays the same while the resume updates instantly.',
      'bottom-start',
      {
        dismissLabel: 'I’ll choose one',
        allowAnchorInteraction: true,
        overlayBody: 'Choose one of these resume styles to preview it with your content.',
        condition: () =>
          window.location.pathname === '/resume-builder' &&
          new URLSearchParams(window.location.search).has('draft'),
      },
    ),
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.score,
      ANCHORS.score,
      6,
      'Your live score follows every meaningful edit, so you can see what is improving without interrupting your work.',
      'left',
      {
        autoCompleteMs: 4200,
        dismissLabel: 'Got it',
        condition: () =>
          window.location.pathname === '/resume-builder' &&
          new URLSearchParams(window.location.search).has('draft'),
      },
    ),
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.chat,
      ANCHORS.chat,
      7,
      'Want help with a change? Ask your resume agent to rewrite, strengthen, shorten, or tailor anything for you.',
      'top-start',
      {
        dismissLabel: 'I’ll try it',
        allowAnchorInteraction: true,
        condition: () =>
          window.location.pathname === '/resume-builder' &&
          new URLSearchParams(window.location.search).has('draft'),
      },
    ),
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.drafts,
      ANCHORS.drafts,
      8,
      'Your work is saved as a draft. Open Drafts anytime to switch versions or come back later.',
      'bottom',
      {
        dismissLabel: 'Got it',
        allowAnchorInteraction: true,
        condition: () =>
          window.location.pathname === '/resume-builder' &&
          new URLSearchParams(window.location.search).has('draft'),
      },
    ),
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.continueToAgent,
      ANCHORS.continueToAgent,
      9,
      'When it feels ready, continue to Job Agent. Your resume will come with you so the agent can start matching and helping you apply.',
      'bottom',
      {
        autoCompleteMs: 3000,
        dismissLabel: 'Sounds good',
        dismissOnAction: true,
        condition: () =>
          window.location.pathname === '/resume-builder' &&
          new URLSearchParams(window.location.search).has('draft'),
      },
    ),
  ])
}

/**
 * Register and resume the account-persisted first-resume journey. `eligible`
 * starts it only for a genuinely empty resume library; after its first milestone
 * is saved, any builder page can resume the remaining steps across reloads and
 * devices without accidentally onboarding established users.
 */
export function useResumeBuilderJourneyFtue(eligible = false, eligibilityPending = false) {
  const hasStarted = useFtueStore((state) => state.seen.has(RESUME_BUILDER_JOURNEY_STEPS.addResume))
  const hasReachedAgent = useFtueStore((state) =>
    state.seen.has(RESUME_BUILDER_JOURNEY_STEPS.continueToAgent),
  )
  const guideFirstResumeToBuilder = shouldGuideFirstResumeToBuilder({
    eligible,
    hasStarted,
    hasReachedAgent,
  })

  useEffect(() => {
    registerJourney()
    const store = useFtueStore.getState()
    if (eligibilityPending) {
      store.startFlow(RESUME_BUILDER_ELIGIBILITY_FLOW, FTUE_FLOW_PRIORITY.primaryJourney, {
        holdWhenEmpty: true,
      })
      return
    }
    if (eligible || (hasStarted && !hasReachedAgent)) {
      store.startFlow(RESUME_BUILDER_JOURNEY_FLOW, FTUE_FLOW_PRIORITY.primaryJourney)
    } else if (store.activeFlow === RESUME_BUILDER_ELIGIBILITY_FLOW) {
      store.startFlow(RESUME_BUILDER_ELIGIBILITY_FLOW, FTUE_FLOW_PRIORITY.primaryJourney, {
        holdWhenEmpty: false,
      })
    }
  }, [eligibilityPending, eligible, hasReachedAgent, hasStarted])

  useEffect(
    () => () => {
      const store = useFtueStore.getState()
      if (store.activeFlow === RESUME_BUILDER_ELIGIBILITY_FLOW) {
        store.startFlow(RESUME_BUILDER_ELIGIBILITY_FLOW, FTUE_FLOW_PRIORITY.primaryJourney, {
          holdWhenEmpty: false,
        })
      }
    },
    [],
  )

  const complete = useCallback((stepId: string) => {
    const state = useFtueStore.getState()
    if (!state.seen.has(stepId)) state.complete(stepId)
  }, [])

  return {
    started: hasStarted,
    guideFirstResumeToBuilder,
    anchors: {
      addResume: useFtueAnchor(ANCHORS.addResume),
      buildResume: useFtueAnchor(ANCHORS.buildResume),
      startMethod: useFtueAnchor(ANCHORS.startMethod),
      renameTitle: useFtueAnchor(ANCHORS.renameTitle),
      directEdit: useFtueAnchor(ANCHORS.directEdit),
      template: useFtueAnchor(ANCHORS.template),
      templateMenu: useFtueAnchor(ANCHORS.template, 'overlay'),
      score: useFtueAnchor(ANCHORS.score),
      chat: useFtueAnchor(ANCHORS.chat),
      drafts: useFtueAnchor(ANCHORS.drafts),
      continueToAgent: useFtueAnchor(ANCHORS.continueToAgent),
    },
    complete,
  }
}
